import { z } from "zod";
import type { CreateLexiconConceptRequest, UpdateLexiconConceptRequest } from "@noa/api-core";
import type { LexiconSubject } from "~/common/lexicon-subject";
import type { AppLanguageType } from "@noa/i18n";
import type { DialogProps } from "~/components/ui";
import { compactFormItemStyle } from "~/utils/form-utils";

export { compactFormItemStyle };

export const lexiconConceptSchema = z.object({
  term_fr: z.string().min(1),
  term_en: z.string().min(1),
  description_fr: z.string().min(1),
  description_en: z.string().min(1),
  subject_id: z.string().min(1),
  acronyms_fr: z.array(z.string()).optional(),
  acronyms_en: z.array(z.string()).optional(),
  synonyms_fr: z.array(z.string()).optional(),
  synonyms_en: z.array(z.string()).optional(),
});

export type LexiconConceptForm = z.infer<typeof lexiconConceptSchema>;

/**
 * Derives a `LexiconSubject`'s `id` from its bilingual pair — French wins,
 * English otherwise.
 *
 * The API subject carries no id of its own, only `fr` and `en`, so the id is a
 * frontend invention: it gives the subject `<Select>` an option value and
 * `fullLexicalConceptFormToRequestBody` a key to look the subject back up by.
 * Both `lexicon-screen.tsx` and `add-lexicon-term-form.tsx` need this same rule;
 * sharing it keeps a future change (e.g. a real `id` field, or flipping the
 * fallback order) from landing in only one of the two surfaces and breaking the
 * other's lookup at submit time.
 */
export function subjectId(subject: { fr: string | null; en: string | null }): string {
  return subject.fr ?? subject.en ?? "";
}

/**
 * Required fields per language tab.
 *
 * Lives here rather than in the fields component so `firstTabWithError` can be
 * imported without pulling a component in — and so the mapping has one home.
 *
 * `subject_id` is deliberately absent: the subject Select renders on both tabs
 * bound to one field, so a missing subject is already visible wherever the user
 * is standing. Listing it per-locale badged both tabs at once for a single
 * error — pointing at a tab with nothing wrong on it — and made
 * `firstTabWithError` return the tab already on screen, so the reveal did
 * nothing.
 */
const REQUIRED_BY_LOCALE = {
  fr: ["term_fr", "description_fr"],
  en: ["term_en", "description_en"],
} as const satisfies Record<"fr" | "en", readonly (keyof LexiconConceptForm)[]>;

export const requiredFieldsFor = (locale: "fr" | "en"): readonly (keyof LexiconConceptForm)[] =>
  REQUIRED_BY_LOCALE[locale];

/**
 * The tab holding a validation error, preferring the one already visible, or
 * `null` if neither does.
 *
 * A tabbed form hides half its fields, so a failed submit can leave the user
 * staring at a valid tab with no feedback. Callers use this in `onInvalid` to
 * reveal the tab that actually failed.
 */
export function firstTabWithError(
  errors: Partial<Record<keyof LexiconConceptForm, unknown>>,
  preferred: "fr" | "en"
): "fr" | "en" | null {
  const holdsError = (locale: "fr" | "en") => REQUIRED_BY_LOCALE[locale].some((f) => errors[f]);
  const other = preferred === "fr" ? "en" : "fr";

  if (holdsError(preferred)) return preferred;
  if (holdsError(other)) return other;
  return null;
}

/**
 * Whether a term the agent flagged might also be worth recording as an acronym.
 *
 * Purely additive: the add-term form seeds the required `term_*` fields from the
 * agent's value either way, and a `true` here only *also* pre-fills the acronym
 * fields. So a wrong guess costs the user one edit rather than leaving a
 * required field blank — which is what made earlier versions of this heuristic
 * worth three separate regression fixes.
 *
 * Deliberately loose as a result. A single all-caps token counts, including an
 * ordinary shouted word ("POMPE"), because there is no reliable way to tell
 * "PLC" from "POMPE" without knowing the domain — and over-offering an acronym
 * chip the user can delete beats missing a real one. What is excluded is the
 * shouted *phrase*: French maintenance jargon writes those as "POMPE À VIDE"
 * and "MARCHE/ARRÊT" alike, and neither is an acronym by any reading.
 *
 * Case is tested with `\p{Lu}` rather than `[A-Z]` so a fully accented acronym
 * ("ÀÉÎ") counts — `[A-Z]` matched "ÉTS" only by way of its ASCII T and S.
 */
export function isAcronym(term: string): boolean {
  if (term !== term.toUpperCase() || !/\p{Lu}/u.test(term)) return false;
  if (/\s/u.test(term)) return false;

  // Two or more multi-letter runs means separated words, i.e. a shouted phrase.
  return (term.match(/\p{L}{2,}/gu) ?? []).length < 2;
}

export function subjectsToOptions(subjects: LexiconSubject[], language: AppLanguageType) {
  return subjects.map((s) => ({ value: String(s.id), label: language === "fr" ? s.fr : s.en }));
}

export type FullLexicalConceptForm = {
  term_fr: string;
  term_en: string;
  description_fr: string;
  description_en: string;
  subject_id: string;
  acronyms_fr?: string[];
  acronyms_en?: string[];
  synonyms_fr?: string[];
  synonyms_en?: string[];
};

export const fullLexicalConceptFormToRequestBody = (
  form: FullLexicalConceptForm,
  subjects: LexiconSubject[]
): CreateLexiconConceptRequest | null => {
  const { subject_id, ...commonValues } = form;
  const subject = subjects.find((s) => s.id === subject_id);
  if (!subject?.en || !subject?.fr) return null;

  return {
    ...commonValues,
    subject_en: subject.en,
    subject_fr: subject.fr,
  };
};

export const fullLexicalConceptFormToUpdateRequestBody = (
  form: FullLexicalConceptForm,
  subjects: LexiconSubject[]
): UpdateLexiconConceptRequest | null => {
  const { subject_id, ...commonValues } = form;
  const subject = subjects.find((s) => s.id === subject_id);
  if (!subject?.en || !subject?.fr) return null;

  return {
    ...commonValues,
    subject_en: subject.en,
    subject_fr: subject.fr,
  };
};

export const lexiconModalClassNames: NonNullable<DialogProps["classNames"]> = {
  body: "max-h-[70vh] overflow-y-auto pr-[1em]",
  footer: "pr-[1em] pt-[0.5em] gap-[1em]",
};
